import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PricingTab = ({ formData, onChange, validationErrors, onBlur }) => {
  const customFields = []; // Add this line - default to empty array if not passed as prop
  const errors = validationErrors || {}; // Add this line - use validationErrors prop or empty object

  const currencies = [
    { value: 'JOD', label: 'دينار أردني (JOD)' },
    { value: 'SAR', label: 'ريال سعودي (SAR)' },
    { value: 'EGP', label: 'جنيه مصري (EGP)' },
    { value: 'USD', label: 'دولار أمريكي (USD)' },
    { value: 'EUR', label: 'يورو (EUR)' }
  ];

  const paymentTerms = [
    { value: 'monthly', label: 'شهري' },
    { value: 'quarterly', label: 'ربع سنوي' },
    { value: 'semi-annual', label: 'نصف سنوي' },
    { value: 'annual', label: 'سنوي' },
    { value: 'one-time', label: 'دفعة واحدة' }
  ];

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat('ar-SA')?.format(num);
  };

  const calculateTotal = () => {
    const price = parseFloat(formData?.price) || 0;
    const commission = parseFloat(formData?.commission) || 0;
    const maintenance = parseFloat(formData?.maintenanceFee) || 0;
    return price + commission + maintenance;
  };

  const pricingCustomFields = customFields?.filter(field => field?.category === 'pricing');

  const renderCustomField = (field) => {
    const value = formData?.customFields?.[field?.id] || '';
    const error = errors?.[`customFields.${field?.id}`];

    switch (field?.type) {
      case 'textarea':
        return (
          <div key={field?.id} className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label} {field?.required && <span className="text-error">*</span>}
            </label>
            <textarea
              className="w-full min-h-24 p-3 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={`أدخل ${field?.label}`}
              value={value}
              onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            />
            {error && <p className="text-sm text-error mt-1">{error}</p>}
          </div>
        );
      default:
        return (
          <Input
            key={field?.id}
            label={field?.label}
            type={field?.type || 'text'}
            placeholder={`أدخل ${field?.label}`}
            value={value}
            onChange={(e) => onChange(`customFields.${field?.id}`, e?.target?.value)}
            error={error}
            required={field?.required}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Input
          label="السعر المطلوب (Asking Price)"
          type="number"
          placeholder="0"
          value={formData?.askingPrice}
          onChange={(e) => onChange('askingPrice', e?.target?.value)}
          error={errors?.askingPrice}
          required
          min="0"
          description="السعر الأولي المعلن للعقار"
        />

        <Input
          label="سعر البيع (Sale Price)"
          type="number"
          placeholder="0"
          value={formData?.salePrice}
          onChange={(e) => onChange('salePrice', e?.target?.value)}
          error={errors?.salePrice}
          required
          min="0"
          description="السعر النهائي للبيع"
        />

        <Input
          label="نسبة الاحتفاظ من العميل (%)"
          type="number"
          placeholder="0"
          value={formData?.clientRetainerPercentage}
          onChange={(e) => onChange('clientRetainerPercentage', e?.target?.value)}
          error={errors?.clientRetainerPercentage}
          min="0"
          max="100"
          description="نسبة الاحتفاظ المتفق عليها من العميل"
        />

        <Input
          label="نسبة الاحتفاظ من المالك (%)"
          type="number"
          placeholder="0"
          value={formData?.ownerRetainerPercentage}
          onChange={(e) => onChange('ownerRetainerPercentage', e?.target?.value)}
          error={errors?.ownerRetainerPercentage}
          min="0"
          max="100"
          description="نسبة الاحتفاظ المتفق عليها من المالك"
        />

        <Input
          label="السعر الأساسي"
          type="number"
          placeholder="0"
          value={formData?.price}
          onChange={(e) => onChange('price', e?.target?.value)}
          onBlur={() => onBlur?.('price')}
          error={errors?.price}
          required
          min="0"
        />

        <Input
          label="السعر المطلوب من المالك"
          type="number"
          placeholder="0"
          value={formData?.priceRequested}
          onChange={(e) => onChange('priceRequested', e?.target?.value)}
          error={errors?.priceRequested}
          min="0"
          description="السعر الذي يطلبه المالك للعقار"
        />

        <Select
          label="العملة"
          options={currencies}
          value={formData?.currency}
          onChange={(value) => onChange('currency', value)}
          error={errors?.currency}
          required
        />

        <Input
          label="عمولة الوكالة"
          type="number"
          placeholder="0"
          value={formData?.commission}
          onChange={(e) => onChange('commission', e?.target?.value)}
          onBlur={() => onBlur?.('commission')}
          min="0"
          description="نسبة أو مبلغ ثابت"
        />

        <Input
          label="رسوم الصيانة"
          type="number"
          placeholder="0"
          value={formData?.maintenanceFee}
          onChange={(e) => onChange('maintenanceFee', e?.target?.value)}
          min="0"
        />

        <Input
          label="التأمين المطلوب"
          type="number"
          placeholder="0"
          value={formData?.securityDeposit}
          onChange={(e) => onChange('securityDeposit', e?.target?.value)}
          min="0"
        />

        <Select
          label="شروط الدفع"
          options={paymentTerms}
          value={formData?.paymentTerms}
          onChange={(value) => onChange('paymentTerms', value)}
        />
      </div>
      <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
        <h3 className="text-base font-heading font-semibold text-foreground mb-3">
          ملخص التكلفة
        </h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">السعر الأساسي:</span>
            <span className="font-medium text-foreground font-data">
              {formatNumber(formData?.price || 0)} {formData?.currency || 'JOD'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">عمولة الوكالة:</span>
            <span className="font-medium text-foreground font-data">
              {formatNumber(formData?.commission || 0)} {formData?.currency || 'JOD'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">رسوم الصيانة:</span>
            <span className="font-medium text-foreground font-data">
              {formatNumber(formData?.maintenanceFee || 0)} {formData?.currency || 'JOD'}
            </span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground">الإجمالي:</span>
            <span className="text-lg font-bold text-primary font-data">
              {formatNumber(calculateTotal())} {formData?.currency || 'JOD'}
            </span>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          خيارات الدفع المتاحة
        </label>
        <div className="space-y-2">
          <Checkbox
            label="الدفع النقدي"
            checked={formData?.paymentOptions?.cash || false}
            onChange={(e) => onChange('paymentOptions', {
              ...formData?.paymentOptions,
              cash: e?.target?.checked
            })}
          />
          <Checkbox
            label="التحويل البنكي"
            checked={formData?.paymentOptions?.bankTransfer || false}
            onChange={(e) => onChange('paymentOptions', {
              ...formData?.paymentOptions,
              bankTransfer: e?.target?.checked
            })}
          />
          <Checkbox
            label="الشيكات"
            checked={formData?.paymentOptions?.check || false}
            onChange={(e) => onChange('paymentOptions', {
              ...formData?.paymentOptions,
              check: e?.target?.checked
            })}
          />
          <Checkbox
            label="التمويل العقاري"
            checked={formData?.paymentOptions?.mortgage || false}
            onChange={(e) => onChange('paymentOptions', {
              ...formData?.paymentOptions,
              mortgage: e?.target?.checked
            })}
          />
        </div>
      </div>

      <div className="bg-muted/30 p-4 rounded-lg border border-border">
        <Checkbox
          label="السعر قابل للتفاوض"
          checked={formData?.negotiable || false}
          onChange={(e) => onChange('negotiable', e?.target?.checked)}
        />
        <p className="text-xs text-muted-foreground mt-2 mr-6">
          حدد هذا الخيار إذا كان السعر قابلاً للتفاوض مع العملاء المحتملين
        </p>
      </div>

      {formData?.askingPrice && formData?.salePrice && formData?.askingPrice > formData?.salePrice && (
        <div className="bg-success/10 p-4 rounded-lg border border-success/20">
          <div className="flex items-start gap-3">
            <Icon name="TrendingDown" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-success mb-1">خصم على السعر</p>
              <p className="text-muted-foreground">
                تم تخفيض السعر بنسبة <span className="font-bold text-success">{Math.round(((formData?.askingPrice - formData?.salePrice) / formData?.askingPrice) * 100)}%</span>
                {' '}من السعر المطلوب ({formatNumber(formData?.askingPrice)} {formData?.currency || 'JOD'})
                {' '}إلى سعر البيع ({formatNumber(formData?.salePrice)} {formData?.currency || 'JOD'})
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          ملاحظات التسعير
        </label>
        <textarea
          className="w-full min-h-24 p-3 md:p-4 rounded-lg border border-input bg-background text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="أي ملاحظات أو تفاصيل إضافية عن التسعير..."
          value={formData?.pricingNotes}
          onChange={(e) => onChange('pricingNotes', e?.target?.value)}
        />
      </div>

      {pricingCustomFields?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
            <span className="w-1 h-4 bg-primary rounded"></span>
            الحقول المخصصة
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {pricingCustomFields?.map(field => renderCustomField(field))}
          </div>
        </div>
      )}

      <div className="bg-muted/50 p-4 md:p-6 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="TrendingUp" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">نصائح التسعير:</p>
            <ul className="list-disc list-inside space-y-1 mr-4">
              <li>قارن الأسعار مع عقارات مشابهة في نفس المنطقة</li>
              <li>ضع في الاعتبار حالة العقار والمرافق المتوفرة</li>
              <li>كن واقعياً في التسعير لجذب المشترين/المستأجرين</li>
              <li>راجع الأسعار بشكل دوري حسب السوق</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTab;